import { assertPigmentCapabilityManifest, canonicalPigmentJson, isBlockNode, isPigmentJsonValue, isPigmentDocument, sha256CanonicalPigmentJson, validatePigmentDocument, } from "@pigment/core";
/** A read-only, portable projection for agents and delegated authoring surfaces. */
export const PIGMENT_AUTHORING_CONTEXT_SCHEMA = "pigment.authoring-context.v1";
/**
 * Derive a JSON-portable authoring map from the public host contract and draft.
 *
 * This is intentionally a pure projection: it does not call a host, mutate a
 * document, issue a preview, or manufacture any authority. Consumers still
 * submit ordinary semantic mutations through the existing client methods.
 */
export function createPigmentAuthoringContext(manifest, source) {
    return createContext(manifest, sourceParts(source));
}
/**
 * Internal async-client projection after source-document hashing has passed.
 * This is intentionally not re-exported from the package root.
 */
export function createPigmentAuthoringContextFromResolved(manifest, source, resolved) {
    assertAuthoringContentEnvelope(source);
    return createContext(manifest, envelopeParts(source, resolved));
}
function createContext(manifest, parts) {
    const trustedManifest = assertPigmentCapabilityManifest(manifest);
    const { page, draftRevision, draftPresent, document, authoringBase } = parts;
    const authoringBasePresent = document !== null;
    const documentPresent = draftPresent ?? true;
    const validation = authoringBasePresent ? validatePigmentDocument(document, trustedManifest) : null;
    const capabilities = new Map(trustedManifest.blocks.map((capability) => [capability.type, capability]));
    const blocks = [];
    if (isPigmentDocument(document)) {
        visitBlocks(document.blocks ?? [], { kind: "blocks" }, null, "$.blocks", capabilities, blocks);
        for (const slot of ["announcement", "header", "footer"]) {
            const override = document.chrome?.[slot];
            if (override?.mode !== "custom")
                continue;
            visitBlocks(override.blocks ?? [], { kind: "chrome", slot }, null, `$.chrome.${slot}.blocks`, capabilities, blocks);
        }
    }
    return {
        schema: PIGMENT_AUTHORING_CONTEXT_SCHEMA,
        manifestVersion: trustedManifest.version,
        manifest: cloneJson(trustedManifest),
        page,
        draftRevision,
        draftPresent,
        authoringBase,
        authoringBasePresent,
        documentPresent,
        blocks,
        availableBlocks: trustedManifest.blocks
            .slice()
            .sort((left, right) => left.type < right.type ? -1 : left.type > right.type ? 1 : 0)
            .map(cloneJson),
        validation: validation === null ? null : cloneJson(validation),
        diagnostics: validation === null ? [] : cloneJson(validation.diagnostics),
    };
}
function sourceParts(source) {
    if (isAuthoringContentEnvelope(source)) {
        assertAuthoringContentEnvelope(source);
        const resolved = resolvePigmentAuthoringBase(source);
        return envelopeParts(source, resolved);
    }
    return { page: null, draftRevision: null, draftPresent: null, document: source, authoringBase: null };
}
function envelopeParts(source, resolved) {
    return {
        page: source.page,
        draftRevision: source.draftRevision,
        draftPresent: source.draft !== null,
        document: resolved?.document ?? null,
        authoringBase: resolved === null ? null : {
            revision: resolved.revision,
            source: resolved.source,
            ...(resolved.sourceDocumentSha256 === undefined ? {} : {
                sourceDocumentSha256: resolved.sourceDocumentSha256,
            }),
            provenance: resolved.provenance,
        },
    };
}
/** Resolve and validate one exact host-authoritative compilation base. */
export function resolvePigmentAuthoringBase(source) {
    const resolved = resolvePigmentAuthoringBaseStructure(source);
    if (resolved?.sourceDocumentSha256) {
        throw new Error("Invalid Pigment authoring base: hash-bound projections require async client verification.");
    }
    return resolved;
}
function resolvePigmentAuthoringBaseStructure(source) {
    assertAuthoringContentEnvelope(source);
    if (!Object.hasOwn(source, "authoringBase")) {
        return source.draft === null ? null : {
            revision: source.draftRevision,
            document: source.draft,
            source: "draft",
            provenance: "legacy-draft",
        };
    }
    const value = source.authoringBase;
    const hasSourceHash = isRecord(value) && Object.hasOwn(value, "sourceDocumentSha256");
    if (!isRecord(value)
        || !hasExactKeys(value, hasSourceHash
            ? ["document", "revision", "source", "sourceDocumentSha256"]
            : ["document", "revision", "source"])
        || !Number.isSafeInteger(value.revision)
        || value.revision < 0
        || !isAuthoringBaseSource(value.source)
        || !isPigmentDocument(value.document)
        || (hasSourceHash && !isSha256(value.sourceDocumentSha256))) {
        throw new Error("Invalid Pigment authoring base: expected an exact revision, Pigment document, source, and optional source SHA-256.");
    }
    if (value.revision !== source.draftRevision) {
        throw new Error("Invalid Pigment authoring base: revision must match draftRevision.");
    }
    if (source.draft !== null) {
        if (value.source !== "draft")
            throw new Error("Invalid Pigment authoring base: a persisted draft requires draft provenance.");
    }
    else if (value.source === "draft") {
        throw new Error("Invalid Pigment authoring base: draft provenance requires a persisted draft.");
    }
    if ((value.source === "published" || value.source === "seed")
        && (value.revision !== 0 || source.draftRevision !== 0)) {
        throw new Error("Invalid Pigment authoring base: published and seed bootstrap bases require revision 0.");
    }
    if (value.source === "published") {
        if (!Object.hasOwn(source, "published") || source.published === null) {
            throw new Error("Invalid Pigment authoring base: published provenance requires published content.");
        }
    }
    else if (value.source === "seed" && Object.hasOwn(source, "published") && source.published !== null) {
        throw new Error("Invalid Pigment authoring base: seed provenance requires absent published content.");
    }
    if (value.source === "seed" && hasSourceHash) {
        throw new Error("Invalid Pigment authoring base: seed provenance cannot bind an unverifiable source document hash.");
    }
    const rawSource = value.source === "draft" ? source.draft
        : value.source === "published" ? source.published
            : null;
    if (rawSource !== null && !sameCanonicalJson(value.document, rawSource) && !hasSourceHash) {
        throw new Error("Invalid Pigment authoring base: a projected draft or published base requires sourceDocumentSha256.");
    }
    return {
        revision: value.revision,
        document: value.document,
        source: value.source,
        ...(hasSourceHash ? { sourceDocumentSha256: value.sourceDocumentSha256 } : {}),
        provenance: "explicit",
    };
}
/** Resolve and cryptographically bind a projected base to its raw host source. */
export async function resolveAndVerifyPigmentAuthoringBase(source) {
    const resolved = resolvePigmentAuthoringBaseStructure(source);
    if (!resolved?.sourceDocumentSha256)
        return resolved;
    const rawSource = resolved.source === "draft" ? source.draft
        : resolved.source === "published" ? source.published
            : null;
    if (rawSource === null || rawSource === undefined) {
        throw new Error("Invalid Pigment authoring base: source document hash has no raw source to verify.");
    }
    const actual = await sha256CanonicalPigmentJson(rawSource);
    if (actual !== resolved.sourceDocumentSha256) {
        throw new Error("Invalid Pigment authoring base: sourceDocumentSha256 does not match the canonical raw source.");
    }
    return resolved;
}
function assertAuthoringContentEnvelope(source) {
    if (typeof source.page !== "string" || source.page.trim() !== source.page || source.page.length === 0
        || !Number.isSafeInteger(source.draftRevision) || source.draftRevision < 0
        || !Object.hasOwn(source, "draft")) {
        throw new Error("Invalid Pigment authoring content envelope: page and draftRevision must be canonical, and draft must be present.");
    }
}
function isAuthoringContentEnvelope(value) {
    return Object.hasOwn(value, "page") || Object.hasOwn(value, "draftRevision") || Object.hasOwn(value, "draft");
}
function isAuthoringBaseSource(value) {
    return value === "draft" || value === "published" || value === "seed";
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isSha256(value) {
    return typeof value === "string" && /^sha256:[0-9a-f]{64}$/u.test(value);
}
function hasExactKeys(value, expected) {
    const actual = Object.keys(value).sort();
    const canonicalExpected = expected.slice().sort();
    return actual.length === canonicalExpected.length
        && actual.every((key, index) => key === canonicalExpected[index]);
}
function sameCanonicalJson(left, right) {
    try {
        return canonicalPigmentJson(left) === canonicalPigmentJson(right);
    }
    catch {
        return false;
    }
}
function visitBlocks(nodes, root, parentId, path, capabilities, output) {
    for (const [index, node] of nodes.entries()) {
        // `isPigmentDocument` has already established this, but keeping the guard
        // here makes the traversal safe if the public core predicate evolves.
        if (!isBlockNode(node))
            continue;
        const capability = capabilities.get(node.type) ?? null;
        const props = node.props ?? {};
        const propsPortable = isPigmentJsonValue(props);
        output.push({
            id: node.id,
            type: node.type,
            path: `${path}[${index}]`,
            root: cloneJson(root),
            parentId,
            index,
            slot: node.slot ?? null,
            props: propsPortable ? cloneJson(props) : null,
            propsPortable,
            known: capability !== null,
            capability: capability === null ? null : cloneJson(capability),
        });
        if (node.children?.length) {
            visitBlocks(node.children, root, node.id, `${path}[${index}].children`, capabilities, output);
        }
    }
}
function cloneJson(value) {
    return JSON.parse(canonicalPigmentJson(value));
}
